import { useCallback, useEffect } from "react";
import { debounce } from "@saviynt/common";
import { useInfiniteQuery } from "@tanstack/react-query";

import { getVaultKeyList } from "../utilities/api/getDataFromApi";
import { INPUT_DEBOUNCE_DELAY } from "../utilities/debounce";

const RECORDS_PER_API_CALL = 100;

const fetchVaultKeys = async ({ pageParam = 0, queryKey }) => {
  const [, searchTerm, limit] = queryKey;
  const response = await getVaultKeyList(searchTerm, limit, pageParam);
  if (response.ok === false) {
    throw new Error("vault key list API call failed.");
  }
  return response;
};

/**
 * A custom hook for fetching against the vault key list API with infinite scroll.
 *
 * @param searchTerm an inputted search keyword
 * @param setVaultKeys a useState hook for setting the vault key values
 */
function useFetchVaultKeyList(searchTerm, setVaultKeys) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isError,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["vaultKeys", searchTerm, RECORDS_PER_API_CALL],
    queryFn: fetchVaultKeys,
    enabled: true,
    getNextPageParam: (lastPage) => {
      // Use nextOffset from response if available
      return lastPage.nextOffset || undefined;
    },
    refetchOnWindowFocus: false,
    retry: 1,
  });

  useEffect(() => {
    if (data) {
      const allKeys = data.pages.flatMap((page) => page.data);
      setVaultKeys(allKeys);
    }
  }, [data, setVaultKeys]);

  const debouncedSearch = useCallback(
    debounce(() => {
      if (!isFetching) {
        refetch();
      }
    }, INPUT_DEBOUNCE_DELAY),
    [refetch, isFetching]
  );

  return {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isError,
    debouncedSearch,
  };
}

export default useFetchVaultKeyList;
